/**
 * Encoding for the sales-territory tree: a client's sales regions, each holding the
 * territories inside it.
 *
 * The tree widget identifies a node by one string. A node is a region plus an optional
 * territory, and both are free text from the client's map, so JSON supplies the
 * escaping, as `encodeSegmentNode` does for the segment tree.
 */

/** Encode one node of the territory tree as a stable key. */
export const encodeTerritoryNode = (salesRegion, territory = null) =>
  JSON.stringify([salesRegion, territory]);

/** Decode a key from `encodeTerritoryNode` into `{ sales_region, territory? }`. */
export const decodeTerritoryNode = (key) => {
  const [sales_region, territory] = JSON.parse(key);
  return territory == null ? { sales_region } : { sales_region, territory };
};

/** How a node reads to a person: the region alone, or "region › territory". */
export const territoryNodeLabel = ({ sales_region, territory }) =>
  territory ? `${sales_region} › ${territory}` : sales_region;

/**
 * Reduce a tree's checked keys to the smallest set of nodes meaning the same.
 *
 * `applyTerritoryCheck` keeps a fully-ticked region's key alongside all of its
 * territories' keys. The region alone already selects them, so they are dropped. A
 * partly ticked region has no key of its own, so its ticked territories survive. A key
 * that does not parse is skipped rather than failing the whole filter.
 */
export const territoryNodesToSelection = (keys) => {
  const nodes = [];
  for (const key of keys ?? []) {
    try {
      nodes.push(decodeTerritoryNode(key));
    } catch {
      // Unreadable key: drop it rather than fail the whole filter.
    }
  }
  const wholeRegions = new Set(nodes.filter((n) => !n.territory).map((n) => n.sales_region));
  return nodes.filter((n) => !n.territory || !wholeRegions.has(n.sales_region));
};

/**
 * Turn a tree selection into the two outlet filters the API takes.
 *
 * Whole regions go to `sales_region` and single territories to `territory`. The API
 * combines the two as a union, which is what the tree shows.
 */
export const territoryNodesToFilters = (keys) => {
  const selection = territoryNodesToSelection(keys);
  const sales_region = selection.filter((n) => !n.territory).map((n) => n.sales_region);
  const territory = selection.filter((n) => n.territory).map((n) => n.territory);
  return {
    ...(sales_region.length ? { sales_region } : {}),
    ...(territory.length ? { territory } : {}),
  };
};

/**
 * Apply one tick or untick to the checked keys, linking regions and territories.
 *
 * A region is exactly the sum of its territories, so ticking a region ticks all of
 * them, and ticking a region's last territory ticks the region. The link is computed
 * from the full tree rather than the rows a search is showing: cascading over a
 * filtered view would take the one visible territory for the whole region, and would
 * drop ticks the search hides.
 */
export const applyTerritoryCheck = (checkedKeys, treeData, nodeKey, checked) => {
  const next = new Set(checkedKeys ?? []);
  const region = (treeData ?? []).find(
    (r) => r.key === nodeKey || (r.children ?? []).some((c) => c.key === nodeKey)
  );
  if (!region) return [...next];
  const childKeys = (region.children ?? []).map((c) => c.key);
  if (nodeKey === region.key) {
    [region.key, ...childKeys].forEach((k) => (checked ? next.add(k) : next.delete(k)));
  } else {
    checked ? next.add(nodeKey) : next.delete(nodeKey);
    childKeys.every((k) => next.has(k)) ? next.add(region.key) : next.delete(region.key);
  }
  return [...next];
};
